import { ChevronRight } from 'lucide-react'

import { SITE_COL_WIDE, SITE_COL_WIDE_FLEX, SITE_TABLE_COLS } from './coverage-table-grid'
import { ageDays } from '../../../lib/issues'
import { SITE_STATUS_META, SITE_STATUS_PILL } from '../../../lib/lens'
import { prettyName } from '../../../lib/mandal-name'

import type { GisSite } from '../../../api/types'

/** One green fill for every completion bar — consistent with the surveyed dot/arc/legend. */
const BAR_FILL = SITE_STATUS_META.surveyed.color

interface CoverageSiteTableRowProps {
  site: GisSite
  onSelect: (id: string) => void
  onHover: (id: string | null) => void
}

/**
 * One worst-first Sites table row (mirrors `IssuesListRow`'s anatomy): status dot + name/officer,
 * mandal, days since the last survey, a completion bar and the survey-status pill. The whole row is
 * one button — clicking opens the site drill overlay (the map flies via the drill selection), and
 * hovering highlights the site's map pin through `onHover`.
 */
export function CoverageSiteTableRow({ site, onSelect, onHover }: CoverageSiteTableRowProps) {
  const meta = SITE_STATUS_META[site.status]
  const pill = SITE_STATUS_PILL[site.status]
  const days = site.lastSurvey ? ageDays(site.lastSurvey) : null
  return (
    <button
      type="button"
      onClick={() => onSelect(site.id)}
      onMouseEnter={() => onHover(site.id)}
      onMouseLeave={() => onHover(null)}
      className={`${SITE_TABLE_COLS} w-full rounded-[12px] border border-transparent px-3 py-[9px] text-left transition-colors duration-[120ms] motion-reduce:transition-none hover:border-gis-line hover:bg-[#f7f9fc] focus-visible:ring-2 focus-visible:ring-blue-500/60`}
    >
      <span className="flex min-w-0 items-center gap-[11px]">
        <span className="h-[10px] w-[10px] flex-none rounded-full" style={{ background: meta.color }} aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-[12.5px] font-semibold text-gis-ink">{site.name}</span>
          <span className="mt-[2px] block truncate text-[10.5px] font-medium text-gis-soft">
            {site.officerName ?? 'Unassigned'}
          </span>
        </span>
      </span>

      <span className={`${SITE_COL_WIDE} truncate text-[11.5px] font-medium text-gis-ink-2`}>{prettyName(site.mandal)}</span>

      <span className={`${SITE_COL_WIDE} text-right`}>
        {days === null ? (
          <span className="text-[11.5px] font-medium text-gis-muted">Never</span>
        ) : (
          <span className="text-[12px] font-bold tabular-nums text-gis-ink-2">{days}d ago</span>
        )}
      </span>

      <span className={`${SITE_COL_WIDE_FLEX} items-center gap-[7px]`}>
        <span className="h-[7px] min-w-0 flex-1 overflow-hidden rounded-full bg-gis-track-2">
          <span className="block h-full rounded-full" style={{ width: `${site.completion}%`, background: BAR_FILL }} />
        </span>
        <span className="min-w-[30px] text-right text-[12px] font-bold tabular-nums text-gis-ink">
          {site.completion}%
        </span>
      </span>

      <span>
        <span className={`inline-flex items-center rounded-full border px-2 py-[3px] text-[10.5px] font-bold tracking-[.01em] ${pill.bg} ${pill.text} ${pill.border}`}>
          {pill.label}
        </span>
      </span>

      <span className="flex items-center justify-end">
        <ChevronRight className="h-[15px] w-[15px] text-gis-soft" aria-hidden />
      </span>
    </button>
  )
}
